import { Message } from "@arco-design/web-react";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isHttpClientError } from "../../../lib/http/errors";
import { verifyBusinessGeo, type GeoBusinessVerifyResult, type GeoOperationStatus } from "../../../shared/business-geo";
import {
  applyScopeLockedSpuDefaults,
  resourcePointDefaultListParams,
  scopeEntityLabel,
  scopeLockedListFilters,
  type ResourcePointListScope,
} from "../config";
import {
  createResourcePoint,
  createResourcePointSku,
  deleteResourcePoint,
  deleteResourcePointSku,
  fetchResourcePoint,
  fetchResourcePoints,
  fetchResourcePointSkus,
  updateResourcePoint,
  updateResourcePointSku,
  verifyResourcePointGeo,
} from "../repo";
import {
  normalizeListFilters,
  resourcePointGeoScenario,
  resourcePointMutationErrorMessage,
  resolvedResourcePointGeoStatus,
  savedResourcePointGeoResult,
  toResourcePointSkuMutation,
  toResourcePointSpuMutation,
  applyResourcePointConfirmedGeo,
  validateSkuMutation,
  validateSpuMutation,
  type ResourcePointSkuMutationErrors,
  type ResourcePointSpuMutationErrors,
} from "../service";
import type {
  ResourcePointListFilters,
  ResourcePointSkuItem,
  ResourcePointSkuMutation,
  ResourcePointSortDirection,
  ResourcePointSortField,
  ResourcePointSpuItem,
  ResourcePointSpuMutation,
} from "../types";

/** SPU 列表查询根 key。 */
export const resourcePointSpuQueryRoot = ["resource-point-management", "spus"] as const;

/** 返回指定 SPU 的 SKU 查询根 key。 */
export const resourcePointSkuQueryRoot = (spuId: string) =>
  ["resource-point-management", "skus", spuId] as const;

/** 返回包含筛选和分页的 SPU 列表查询 key。 */
export const resourcePointSpuListQueryKey = (
  params: ReturnType<typeof normalizeListFilters>,
) => [...resourcePointSpuQueryRoot, params] as const;

/** 新增 SPU 表单的空载荷。 */
export const emptyResourcePointSpuMutation: ResourcePointSpuMutation = {
  pointType: "",
  pointSubType: undefined,
  pointName: "",
  pointShortName: "",
  headImageUrl: "",
  country: "中国",
  province: "",
  city: "",
  district: "",
  address: "",
  lng: undefined,
  lat: undefined,
  visitMode: "",
  teachingTags: "",
  isDisabled: false,
  status: "active",
  remark: "",
};

/** 新增 SKU 表单的空载荷。 */
export const emptyResourcePointSkuMutation: ResourcePointSkuMutation = {
  skuName: "",
  ticketResourceType: "entrance_ticket",
  travelerRole: "student",
  settlementUnit: "人",
  reservationRequired: false,
  reservationInfoMarkdown: "",
  minGroupSize: undefined,
  maxGroupSize: undefined,
  isDisabled: false,
  status: "active",
  remark: "",
};

/** 资源点写操作校验结果。 */
export type ResourcePointMutationResult<Errors> =
  | { ok: true }
  | { ok: false; errors?: Errors };

type ResourcePointSkuUpdateVariables = {
  spuId: string;
  skuId: string;
  payload: ResourcePointSkuMutation;
};

/** 管理 SPU 列表查询、筛选、分页和全部写操作。 */
export function useResourcePointManagementRuntime(
  deepLinkSpuId?: string,
  scope: ResourcePointListScope = "all",
) {
  const queryClient = useQueryClient();
  const entityLabel = scopeEntityLabel(scope);
  const initialFilters = scopeLockedListFilters(scope);
  const [draftFilters, setDraftFilters] = useState<ResourcePointListFilters>(initialFilters);
  const [submittedFilters, setSubmittedFilters] = useState<ResourcePointListFilters>(initialFilters);
  const [page, setPage] = useState<number>(resourcePointDefaultListParams.page);
  const [pageSize, setPageSize] = useState<number>(resourcePointDefaultListParams.pageSize);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [skuToggleErrorMessages, setSkuToggleErrorMessages] = useState<Record<string, string>>({});
  const [geoStatus, setGeoStatus] = useState<GeoOperationStatus>("idle");
  const [geoResult, setGeoResult] = useState<GeoBusinessVerifyResult>();
  const [geoOwnerSpuId, setGeoOwnerSpuId] = useState<string>();
  const [geoAdopted, setGeoAdopted] = useState(false);
  const [geoVerifyingSpuId, setGeoVerifyingSpuId] = useState<string>();
  const togglePendingSkuIdsRef = useRef<Set<string>>(new Set());
  const [togglePendingSkuIds, setTogglePendingSkuIds] = useState<Set<string>>(() => new Set());

  const setSkuToggleError = (spuId: string, message: string) => {
    setSkuToggleErrorMessages((current) => {
      if (message) return { ...current, [spuId]: message };
      if (!(spuId in current)) return current;
      const next = { ...current };
      delete next[spuId];
      return next;
    });
  };

  const listParams = useMemo(
    () => normalizeListFilters({ ...submittedFilters, page, pageSize }, scope),
    [page, pageSize, scope, submittedFilters],
  );
  const listQuery = useQuery({
    queryKey: resourcePointSpuListQueryKey(listParams),
    queryFn: () => fetchResourcePoints(listParams),
    placeholderData: (previousData) => previousData,
  });
  const deepLinkedSpuQuery = useQuery({
    queryKey: ["resource-point-management", "spu", deepLinkSpuId],
    queryFn: () => fetchResourcePoint(deepLinkSpuId as string),
    enabled: Boolean(deepLinkSpuId),
  });

  const invalidateSpus = () => queryClient.invalidateQueries({ queryKey: resourcePointSpuQueryRoot });
  const invalidateSku = (spuId: string) =>
    queryClient.invalidateQueries({ queryKey: resourcePointSkuQueryRoot(spuId) });

  const createSpuMutation = useMutation({
    mutationFn: (payload: ResourcePointSpuMutation) => createResourcePoint(payload),
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateSpus();
    },
  });
  const updateSpuMutation = useMutation({
    mutationFn: ({ spuId, payload }: { spuId: string; payload: ResourcePointSpuMutation }) =>
      updateResourcePoint(spuId, payload),
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateSpus();
    },
  });
  const deleteSpuMutation = useMutation({
    mutationFn: (spuId: string) => deleteResourcePoint(spuId),
    onSuccess: invalidateSpus,
  });
  const createSkuMutation = useMutation({
    mutationFn: ({ spuId, payload }: { spuId: string; payload: ResourcePointSkuMutation }) =>
      createResourcePointSku(spuId, payload),
    onSuccess: async (_, { spuId }) => {
      await Promise.all([invalidateSku(spuId), invalidateSpus()]);
    },
  });
  const updateSkuMutation = useMutation({
    mutationFn: ({ spuId, skuId, payload }: ResourcePointSkuUpdateVariables) =>
      updateResourcePointSku(spuId, skuId, payload),
    onSuccess: async (_, { spuId }) => {
      await Promise.all([invalidateSku(spuId), invalidateSpus()]);
    },
  });
  const toggleSkuDisabledMutation = useMutation({
    mutationFn: ({ spuId, skuId, payload }: ResourcePointSkuUpdateVariables) =>
      updateResourcePointSku(spuId, skuId, payload),
    onSuccess: async (_, { spuId }) => {
      await Promise.all([invalidateSku(spuId), invalidateSpus()]);
    },
  });
  const deleteSkuMutation = useMutation({
    mutationFn: ({ spuId, skuId }: { spuId: string; skuId: string }) =>
      deleteResourcePointSku(spuId, skuId),
    onSuccess: async (_, { spuId }) => {
      await Promise.all([invalidateSku(spuId), invalidateSpus()]);
    },
  });
  const geoMutation = useMutation({
    mutationFn: ({
      spu,
      payload,
    }: {
      spu?: ResourcePointSpuItem;
      payload: ResourcePointSpuMutation;
    }) => payload.pointType === "火车站"
      ? verifyResourcePointGeo(spu?.resourcePointSpuId, payload)
      : spu
      ? verifyResourcePointGeo(spu.resourcePointSpuId)
      : verifyBusinessGeo({
        province: payload.province ?? "",
        city: payload.city,
        district: payload.district,
        areaName: payload.pointName,
        scenario: resourcePointGeoScenario(payload.pointType),
        contextName: payload.pointType,
        sourceAddress: payload.address,
        originalLongitude: payload.lng,
        originalLatitude: payload.lat,
        pointSubType: payload.pointSubType,
        shortName: payload.pointShortName,
      }),
  });

  const pageData = listQuery.data ?? { items: [], total: 0, page, pageSize };
  const mutationPending = createSpuMutation.isPending
    || updateSpuMutation.isPending
    || deleteSpuMutation.isPending
    || createSkuMutation.isPending
    || updateSkuMutation.isPending
    || deleteSkuMutation.isPending;

  /** 执行一次考察点 GEO，并把面板状态写成处理中或最终结论。 */
  async function runVerifySpuGeo(
    spu: ResourcePointSpuItem | undefined,
    payload: ResourcePointSpuMutation,
  ): Promise<GeoBusinessVerifyResult> {
    setGeoOwnerSpuId(spu?.resourcePointSpuId);
    setGeoAdopted(false);
    setGeoStatus("processing");
    setGeoResult(undefined);
    try {
      const result = await geoMutation.mutateAsync({ spu, payload });
      setGeoResult(result);
      setGeoStatus(result.status);
      return result;
    } catch (error) {
      const message = isGeoTimeoutError(error)
        ? "GEO 核验耗时较长已超时，请稍后重试。"
        : "GEO 核验暂时失败，请稍后重试。";
      const failure: GeoBusinessVerifyResult = {
        scenario: resourcePointGeoScenario(payload.pointType),
        status: "retryable_failure",
        conclusion: message,
        llmConclusion: message,
        amapStandardAddress: "",
        province: "",
        city: "",
        district: "",
        coordinateSystem: "unknown",
        confidenceScore: 0,
        correctionReason: message,
        retryable: true,
        candidates: [],
      };
      setGeoResult(failure);
      setGeoStatus("retryable_failure");
      return failure;
    }
  }

  return {
    draftFilters,
    items: pageData.items,
    deepLinkedSpu: deepLinkedSpuQuery.data,
    total: pageData.total,
    page,
    pageSize,
    sortField: listParams.sortField,
    sortDirection: listParams.sortDirection,
    loading: listQuery.isPending,
    fetching: listQuery.isFetching,
    errorMessage: listQuery.error ? "考察点列表加载失败，请稍后重试。" : "",
    mutationErrorMessage,
    skuToggleErrorMessages,
    mutationPending,
    geoPending: geoMutation.isPending,
    geoVerifyingSpuId,
    geoOwnerSpuId,
    geoStatus,
    geoResult,
    togglePendingSkuIds: togglePendingSkuIds as ReadonlySet<string>,
    setDraftFilters,
    clearMutationError() {
      setMutationErrorMessage("");
    },
    clearGeoResult() {
      setGeoStatus("idle");
      setGeoResult(undefined);
      setGeoOwnerSpuId(undefined);
      setGeoAdopted(false);
    },
    hydrateSavedGeo(spu?: ResourcePointSpuItem) {
      if (
        spu
        && geoOwnerSpuId === spu.resourcePointSpuId
        && geoStatus !== "idle"
      ) {
        return;
      }
      const snapshot = spu ? savedResourcePointGeoResult(spu) : undefined;
      setGeoStatus(snapshot ? "confirmed" : "idle");
      setGeoResult(snapshot);
      setGeoOwnerSpuId(snapshot ? spu?.resourcePointSpuId : undefined);
      setGeoAdopted(false);
    },
    clearSkuToggleError(spuId: string) {
      setSkuToggleError(spuId, "");
    },
    handleSearch() {
      setSubmittedFilters({
        ...draftFilters,
        ...scopeLockedListFilters(scope),
        page: undefined,
        pageSize: undefined,
      });
      setPage(1);
    },
    handleReset() {
      const locked = scopeLockedListFilters(scope);
      setDraftFilters(locked);
      setSubmittedFilters(locked);
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(1, nextPage));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    handleSort(sortField: ResourcePointSortField, sortDirection: ResourcePointSortDirection) {
      const nextSort = { sortField, sortDirection };
      setDraftFilters((current) => ({ ...current, ...nextSort }));
      setSubmittedFilters((current) => ({
        ...current,
        ...scopeLockedListFilters(scope),
        ...nextSort,
      }));
      setPage(1);
    },
    handleCreateSpu(payload: ResourcePointSpuMutation) {
      const nextPayload = withResolvedGeoStatus(
        applyScopeLockedSpuDefaults(scope, payload),
        geoStatus,
        geoAdopted,
      );
      return runValidatedMutation(
        nextPayload,
        (value) => validateSpuMutation(
          value,
          undefined,
          entityLabel,
          scope === "all" || scope === "other",
        ),
        () => createSpuMutation.mutateAsync(nextPayload),
        setMutationErrorMessage,
      );
    },
    handleUpdateSpu(spu: ResourcePointSpuItem, payload: ResourcePointSpuMutation) {
      const nextPayload = withResolvedGeoStatus(payload, geoStatus, geoAdopted);
      return runValidatedMutation(
        nextPayload,
        (value) => validateSpuMutation(value, spu.pointSubType, entityLabel),
        () => updateSpuMutation.mutateAsync({ spuId: spu.resourcePointSpuId, payload: nextPayload }),
        setMutationErrorMessage,
      );
    },
    handleDeleteSpu(spuId: string) {
      return runDeleteMutation(
        () => deleteSpuMutation.mutateAsync(spuId),
        setMutationErrorMessage,
      );
    },
    handleCreateSku(spuId: string, payload: ResourcePointSkuMutation) {
      return runValidatedMutation(
        payload,
        validateSkuMutation,
        () => createSkuMutation.mutateAsync({ spuId, payload }),
        setMutationErrorMessage,
      );
    },
    handleUpdateSku(spuId: string, skuId: string, payload: ResourcePointSkuMutation) {
      const currentSku = queryClient.getQueryData<ResourcePointSkuItem[]>(
        resourcePointSkuQueryRoot(spuId),
      )?.find((sku) => sku.resourcePointSkuId === skuId);
      return runValidatedMutation(
        payload,
        (value) => validateSkuMutation(value, currentSku),
        () => updateSkuMutation.mutateAsync({ spuId, skuId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleToggleSkuDisabled(spuId: string, sku: ResourcePointSkuItem) {
      const skuId = sku.resourcePointSkuId;
      if (togglePendingSkuIdsRef.current.has(skuId)) {
        return { ok: false } as const;
      }
      togglePendingSkuIdsRef.current.add(skuId);
      setTogglePendingSkuIds(new Set(togglePendingSkuIdsRef.current));
      setSkuToggleError(spuId, "");
      const payload = toResourcePointSkuMutation(sku, { isDisabled: !sku.isDisabled });
      try {
        return await runValidatedMutation(
          payload,
          (value) => validateSkuMutation(value, sku),
          () => toggleSkuDisabledMutation.mutateAsync({ spuId, skuId, payload }),
          (message) => setSkuToggleError(spuId, message),
        );
      } finally {
        togglePendingSkuIdsRef.current.delete(skuId);
        setTogglePendingSkuIds(new Set(togglePendingSkuIdsRef.current));
      }
    },
    handleDeleteSku(spuId: string, skuId: string) {
      return runDeleteMutation(
        () => deleteSkuMutation.mutateAsync({ spuId, skuId }),
        setMutationErrorMessage,
      );
    },
    async handleVerifySpuGeo(
      spu: ResourcePointSpuItem | undefined,
      payload: ResourcePointSpuMutation,
    ) {
      return runVerifySpuGeo(spu, payload);
    },
    handleAdoptGeoCandidate() {
      setGeoAdopted(true);
    },
    async handleVerifyListSpuGeo(spu: ResourcePointSpuItem) {
      if (geoMutation.isPending) {
        return { saved: false as const };
      }
      setMutationErrorMessage("");
      setGeoVerifyingSpuId(spu.resourcePointSpuId);
      try {
        const result = await runVerifySpuGeo(spu, toResourcePointSpuMutation(spu));
        const canSave = result.status === "confirmed"
          && result.longitude != null
          && result.latitude != null;
        if (!canSave) {
          return { result, saved: false as const };
        }
        const payload = {
          ...applyResourcePointConfirmedGeo(toResourcePointSpuMutation(spu), result),
          geoStatus: "confirmed" as const,
        };
        const saved = await runValidatedMutation(
          payload,
          (value) => validateSpuMutation(value, spu.pointSubType, entityLabel),
          () => updateSpuMutation.mutateAsync({
            spuId: spu.resourcePointSpuId,
            payload,
          }),
          setMutationErrorMessage,
        );
        if (!saved.ok) {
          return { result, saved: false as const };
        }
        setGeoStatus("idle");
        setGeoResult(undefined);
        setGeoOwnerSpuId(undefined);
        return { result, saved: true as const };
      } finally {
        setGeoVerifyingSpuId(undefined);
      }
    },
  };
}

/** 把当前 GEO 面板结论写入保存载荷。 */
function withResolvedGeoStatus(
  payload: ResourcePointSpuMutation,
  geoStatus: GeoOperationStatus,
  geoAdopted: boolean,
): ResourcePointSpuMutation {
  return {
    ...payload,
    geoStatus: resolvedResourcePointGeoStatus(payload, geoStatus, geoAdopted),
  };
}

/** 判断 GEO 请求是否因超时中断。 */
function isGeoTimeoutError(error: unknown): boolean {
  if (isHttpClientError(error)) {
    return error.code === "ECONNABORTED" || /timeout/i.test(error.message);
  }
  return error instanceof Error && /timeout/i.test(error.message);
}

/** 资源点管理运行时公开契约。 */
export type ResourcePointManagementRuntime = ReturnType<typeof useResourcePointManagementRuntime>;

/** 仅在调用方启用后查询指定 SPU 的 SKU。 */
export function useResourcePointSkuQuery(spuId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: resourcePointSkuQueryRoot(spuId),
    queryFn: () => fetchResourcePointSkus(spuId),
    enabled,
  });
  return {
    items: query.data ?? [],
    loading: query.isPending && enabled,
    fetching: query.isFetching,
    errorMessage: query.error ? "考察点 SKU 加载失败，请稍后重试。" : "",
  };
}

/** 执行带 service 校验的写操作并返回表单可直接消费的结果。 */
async function runValidatedMutation<Payload, Errors extends Record<string, string | undefined>>(
  payload: Payload,
  validate: (value: Payload) => Errors,
  execute: () => Promise<unknown>,
  setErrorMessage: (message: string) => void,
): Promise<ResourcePointMutationResult<Errors>> {
  const errors = validate(payload);
  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  setErrorMessage("");
  try {
    await execute();
    return { ok: true };
  } catch (error) {
    setErrorMessage(resourcePointMutationErrorMessage(error));
    return { ok: false };
  }
}

/** 执行删除并统一映射稳定错误码。 */
async function runDeleteMutation(
  execute: () => Promise<unknown>,
  setErrorMessage: (message: string) => void,
): Promise<boolean> {
  setErrorMessage("");
  try {
    await execute();
    return true;
  } catch (error) {
    setErrorMessage(resourcePointMutationErrorMessage(error));
    return false;
  }
}

/** 导出资源点管理运行时使用的领域类型。 */
export type {
  ResourcePointSkuMutationErrors,
  ResourcePointSpuMutationErrors,
};
